<template>
  <div>
    <v-card-title>
      <h2>Contact Files</h2>
    </v-card-title>
    <br />
    <v-layout
      row wrap
    >
      <v-flex
        sm12
        md3
        style="padding:0 5px 0 5px;"
      >
        <v-card>
          <v-toolbar
            class="primary-color"
            dense
            flat
            dark
          >
            Search &amp; Filtering
          </v-toolbar>
          <v-card-text>
            <v-text-field
              v-model="searchText"
              append-icon="search"
              label="Search Text"
              single-line
              hide-details
              @blur="search"
              v-on:keyup.enter="search"
            />
            <v-select
              v-model="selectedField"
              :items="searchFields"
            />
            <v-combobox
              v-model.trim="filterTags"
              :items="tagResults"
              :search-input.sync="tagSearch"
              label="Search by Tag"
              item-text="tag"
              item-value="tag"
              :return-object="false"
              flat
              small-chips
              hide-selected
              auto-select-first
              @input="tagSearch = ''"
            />
            <v-radio-group
              style="margin:0;"
              v-model="filterRead"
            >
              <template v-slot:label>
                <div>Filter Read Status</div>
              </template>
              <v-layout
                row wrap
              >
                <v-flex
                  sm6
                >
                  <v-radio
                    label="Read"
                    value="true"
                  />
                </v-flex>
                <v-flex
                  sm6
                >
                  <v-radio
                    label="Unread"
                    value="false"
                  />
                </v-flex>
              </v-layout>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              @click="searchText='';tagSearch = '';filterTags=null;filterRead=null;search()"
            >
              Clear
            </v-btn>
            <v-btn
              color="primary"
              @click="search"
            >
              Search
            </v-btn>
            <v-spacer/>
          </v-card-actions>
        </v-card>
      </v-flex>
      <v-flex
        sm12
        md9
      >
        <v-card>
          <v-data-table
            :items="files"
            :headers="headers"
            :pagination.sync="pagination"
            :total-items="totalContacts"
            :rows-per-page-items="paginationStrings"
            :loading="loading"
            disable-initial-sort
          >
            <template slot="no-data">
              <tr>
                <td colspan="4">
                  <div style="text-align:center;padding:25px;">
                    <v-icon size="125px">find_in_page</v-icon>
                    <h3>No Results</h3>
                  </div>
                </td>
              </tr>
            </template>
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                :style="(props.item.tags.length > 0) ? 'border-bottom:0;' : null"
              >
                <td>
                  <a @click="getFile(props.item.contact, props.item.id, props.item.name)">
                    {{ props.item.name }}
                  </a>
                </td>
                <td>
                  <router-link :to="{ name: 'Contact', params: { id: props.item.contact.id } }">
                    <b>{{ props.item.contact.full_name }}</b>
                  </router-link><br />
                  <small>{{ props.item.contact.cuid }}</small>
                </td>
                <td>
                  {{ convertToLocal(props.item.created_at) }}<br />
                  <small style="color:black;">{{props.item.created_by_name}}</small>
                </td>
                <td>
                  {{ convertToLocal(props.item.updated_at) }}<br />
                  <small style="color:black;">{{props.item.updated_by_name}}</small>
                </td>
                <td>
                  <v-tooltip left>
                    <v-icon
                      :color="props.item.read ? '#cfcfcf' : '#1976d2'"
                      v-on="on"
                      @click="updateReadStatus(props.item)"
                      slot="activator"
                    >
                      {{ props.item.read ? 'visibility_off' : 'visibility' }}
                    </v-icon>
                    <span>{{ props.item.read ? 'Mark Unread' : 'Mark Read' }}</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr
                v-if="props.item.tags.length > 0"
              >
                <td colspan="5">
                  <span
                    v-for="tag in resortTags(props.item.tags)"
                    :key="tag"
                  >
                    <v-chip
                      label
                      small
                      :color="(tag === filterTags) ? 'yellow' : null"
                      :outline="tag.includes(':')"
                    >
                      {{tag}}
                    </v-chip>
                  </span>
                </td>
              </tr>
            </template>
            <template slot="actions-append">
              <v-tooltip left>
                <span
                  slot="activator"
                >
                  <v-btn
                    round
                    flat
                    fab
                    small
                    @click="downloadCSV()"
                  >
                    <v-icon
                      color="green"
                    >
                      table_chart
                    </v-icon>
                  </v-btn>
                </span>
                <span>Download CSV<br /><small>Of Current Search Terms</small></span>
              </v-tooltip>
              <v-spacer />
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
      v-model="downloadingCSV"
      persistent
      width="300"
    >
      <v-card
        color="white"
      >
        <v-card-text>
          Generating CSV File...<br/>
          <small>Depending on your query, this may take some time.</small>
          <v-progress-linear
            indeterminate
            color="blue"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'
import pagination from '@/components/global/pagination'

const SEARCH_FIELDS = ['Any', 'File Name', 'Contact First Name', 'Contact Last Name', 'CUID']

export default {
  name: 'ContactFilesList',
  mixins: [
    pagination
  ],
  data () {
    return {
      files: [],
      pagination: {},
      searchText: '',
      loading: true,
      downloadingCSV: false,
      paginationStrings: [
        10, 25, 50
      ],
      headers: [
        { text: 'File Name', value: 'upload' },
        { text: 'Contact', value: 'contact__last_name' },
        { text: 'Created', value: 'created_at' },
        { text: 'Updated', value: 'updated_at' },
        { text: 'Read', sortable: false, width: 75}
      ],
      filter: {},
      filterTags: null,
      filterRead: null,
      tagSearch: null,
      tagResults: [],
      searchFields: SEARCH_FIELDS,
      selectedField: SEARCH_FIELDS[0]
    }
  },
  created () {
    this.documentTitle('Contacts')
  },
  computed: {
    totalContacts () {
      return this.pagination.totalItems
    }
  },
  watch: {
    tagSearch () {
      if (this.tagSearch) {
        this.fetchTags()
      }
    }
  },
  methods: {
    fetch () {
      this.loading = true
      api.userform.contactFilesPage.list({
        search: this.searchText, 
        ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy, 
        pageNum: this.pagination.page, 
        count: this.pagination.rowsPerPage, 
        filter: this.filter || null, 
        searchField: this.selectedField
      })
        .then(data => {
          this.files = data.results
          this.pagination.page = data.page_number
          this.pagination.totalItems = data.count
          this.loading = false
          this.componentCreated = true
        })
    },
    fetchTags () {
      api.userform.contactTags.get(this.tagSearch)
        .then(tagResults => {
          this.tagResults = tagResults
        })
    },
    getFile (contact, fileId, name) {
      this.loading = true
      api.userform.contact.downloadFile(contact, fileId)
      .then(response => {
        let blob = new Blob([response.data])
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = name
        link.click()
        this.loading = false
      })
    },
    search () {
      this.pagination.page = 1
      var queryParam = {}, filterQueryParam = {}
      if (this.searchText) {
        queryParam['search'] = this.searchText
        if (this.selectedField)
          queryParam['field'] = this.selectedField
      }
      if (this.filterTags) {filterQueryParam['contact__tags__name'] = this.filterTags} else {delete filterQueryParam['contact__tags__name']}
      if (this.filterRead) {filterQueryParam['read'] = this.filterRead} else {delete filterQueryParam['read']}
      if (Object.keys(filterQueryParam).length !== 0) {queryParam['filter'] = JSON.stringify(filterQueryParam)}
      this.$router.push({ query: queryParam })
    },
    updateReadStatus (object) {
      object.read = !object.read
      api.userform.contactFilesPage.save({id: object.id, read: object.read})
    },
    downloadCSV () {
      this.downloadingCSV = true
      api.userform.contactFilesPage.downloadCsv({
        search: this.searchText, ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy, pageNum: this.pagination.page, count: this.pagination.rowsPerPage, filter: this.filter || null
      })
        .then(response => {
          this.downloadingCSV = false
          let blob = new Blob([response.data])
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'Export_ContactFileSearch.csv'
          link.click()
        })
    },
  }
}
</script>

<style>

.search-bar {
  margin-top: 1em;
}

table {
  margin-top: 1em;
}

</style>
